/**
 * 角色卡编辑视图（T07）。
 * -----------------------------------------------------------------------------
 * 编辑 name / avatar / description / systemPrompt / tags，并内嵌「世界书」子编辑器：
 * 增删改条目的 keywords（逗号分隔）、content、enabled、priority。
 * 所有可见部件通过 `Framed.tsx` 登记进组件清单（390x844 逻辑坐标）。
 * ============================================================================= */

import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';

import { newId } from '../../core/ids';
import { useFeatureFlags } from '../../core/featureFlags';
import type { Preset, WorldBookEntry } from '../../core/types';
import { ImageCropDialog } from '../../ui/ImageCropDialog';
import { worldBookEntryBoxes } from './componentMeta';
import { FramedBox, FramedButton, FramedHeading, FramedInput, FramedSwitch, FramedText, FramedTextarea } from './Framed';
import { splitKeywords } from './keywordText';
import {
  ADD_ENTRY_BUTTON_BOX,
  AUTOMATION_TOP,
  INTIMACY_TOP,
  AUTOMATION_SECTION_HEIGHT,
  EDITOR_BACK_BOX,
  EDITOR_ERROR_BOX,
  EDITOR_HEADER_BOX,
  EDITOR_NOTICE_BOX,
  EDITOR_SAVE_BOX,
  EDITOR_TITLE_BOX,
  FIELD_AVATAR_LABEL_BOX,
  FIELD_DESC_BOX,
  FIELD_DESC_LABEL_BOX,
  FIELD_NAME_BOX,
  FIELD_NAME_LABEL_BOX,
  FIELD_PROMPT_BOX,
  FIELD_PROMPT_LABEL_BOX,
  FIELD_TAGS_BOX,
  FIELD_TAGS_LABEL_BOX,
  MODEL_CONFIG_BUTTON_BOX,
  FORM_SCROLL_BOX,
  FORM_SCROLL_TOP,
  PAGE_BOX,
  WORLD_BOOK_ENTRY_STEP,
  WORLD_BOOK_ENTRY_TOP,
  WORLD_BOOK_TITLE_BOX,
  IMPORT_WORLD_BOOK_EDITOR_BOX,
} from './layout';
import { PresetAutomationEditor } from './PresetAutomationEditor';
import { automationEditorHeight } from './automationLayout';
import { RoleModelEditor } from './RoleModelEditor';
import { IntimacyControl } from '../intimacy/IntimacyControl';

export interface PresetEditorViewProps {
  draft: Preset;
  isNew: boolean;
  error: string | null;
  notice?: string | null;
  importing?: boolean;
  saving?: boolean;
  initialModelOpen?: boolean;
  onChange: (next: Preset) => void;
  onSave: () => void;
  onCancel: () => void;
  onImportWorldBook: () => void;
}

// 世界书条目几何从 `componentMeta.ts` 的 `worldBookEntryBoxes()` 读取
// （视图定位与清单登记共用同一份声明，不再在视图里内联坐标）。
export function PresetEditorView({
  draft,
  isNew,
  error,
  notice = null,
  importing = false,
  saving = false,
  initialModelOpen = false,
  onChange,
  onSave,
  onCancel,
  onImportWorldBook,
}: PresetEditorViewProps): ReactElement {
  const flags = useFeatureFlags();
  const [rawKeywords, setRawKeywords] = useState<Record<string, string>>({});
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const avatarInput = useRef<HTMLInputElement | null>(null);
  const errorContent = useRef<HTMLSpanElement | null>(null);
  const [errorHeight, setErrorHeight] = useState(EDITOR_ERROR_BOX.height);
  const [modelOpen, setModelOpen] = useState(initialModelOpen);
  const entries = draft.worldBook ?? [];
  useLayoutEffect(() => {
    const content = errorContent.current;
    if (content === null) return;
    const measure = (): void => setErrorHeight(Math.min(180, Math.max(EDITOR_ERROR_BOX.height, content.offsetHeight + 16)));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [error]);
  const errorOffset = error === null ? 0 : errorHeight - EDITOR_ERROR_BOX.height;
  useLayoutEffect(() => { if (initialModelOpen) setModelOpen(true); }, [initialModelOpen]);

  const patch = (portion: Partial<Preset>): void => {
    onChange({ ...draft, ...portion });
  };

  const patchEntry = (index: number, portion: Partial<WorldBookEntry>): void => {
    patch({ worldBook: entries.map((entry, i) => (i === index ? { ...entry, ...portion } : entry)) });
  };

  const addEntry = (): void => {
    patch({
      worldBook: [
        ...entries,
        { id: newId(), keywords: [], content: '', enabled: true, priority: 0 },
      ],
    });
  };

  const removeEntry = (index: number): void => {
    const removed = entries[index];
    if (removed !== undefined) {
      setRawKeywords((prev) => {
        const next = { ...prev };
        delete next[removed.id];
        return next;
      });
    }
    patch({ worldBook: entries.filter((_, i) => i !== index) });
  };

  const worldBookOffset = automationEditorHeight(draft, { image: flags.imageInitiative.enabled, tts: flags.ttsInitiative.enabled }) - AUTOMATION_SECTION_HEIGHT;
  const canvasHeight = WORLD_BOOK_ENTRY_TOP + worldBookOffset
    + entries.length * WORLD_BOOK_ENTRY_STEP + 32 - FORM_SCROLL_TOP;

  if (modelOpen) return <RoleModelEditor draft={draft} onCancel={() => setModelOpen(false)} onApply={modelConfig => {
    patch({ modelConfig }); setModelOpen(false);
  }} />;

  return (
    <FramedBox
      meta={{
        id: 'presets.editor.root',
        page: 'presets',
        role: 'container',
        label: '联系人编辑页面容器',
        layout: { ...PAGE_BOX },
        zIndex: 2,
        notes: '坐标含顶部 24dp 状态栏偏移；安卓用 Fragment 根 View',
      }}
      className="zs-page"
      surface="page"
    >
      <FramedBox
        meta={{
          id: 'presets.editor.header',
          page: 'presets',
          role: 'container',
          label: '编辑页标题栏',
          layout: { ...EDITOR_HEADER_BOX },
          zIndex: 5,
          notes: '安卓用 MaterialToolbar',
        }}
        className="zs-header"
      />

      <FramedHeading
        level={1}
        meta={{
          id: 'presets.editor.title',
          page: 'presets',
          role: 'text',
          label: isNew ? '新建联系人' : '编辑联系人',
          layout: { ...EDITOR_TITLE_BOX },
          zIndex: 6,
          notes: '页面标题；安卓用 Toolbar title',
        }}
        className="zs-title"
      >
        {isNew ? '新建联系人' : '编辑联系人'}
      </FramedHeading>

      <FramedButton
        meta={{
          id: 'presets.editor.backButton',
          page: 'presets',
          role: 'button',
          label: '返回',
          layout: { ...EDITOR_BACK_BOX },
          zIndex: 6,
          notes: '返回列表；安卓用 Toolbar navigation icon',
        }}
        className="zs-button zs-button-ghost"
        onClick={onCancel}
        disabled={saving}
      >
        返回
      </FramedButton>

      <FramedButton
        meta={{
          id: 'presets.editor.saveButton',
          page: 'presets',
          role: 'button',
          label: '保存',
          layout: { ...EDITOR_SAVE_BOX },
          zIndex: 6,
          props: ['disabled'],
          notes: '保存到 presetRepo；安卓用 MaterialButton',
        }}
        className="zs-button zs-button-primary"
        onClick={onSave}
        disabled={saving}
      >
        {saving ? '同步中' : '保存'}
      </FramedButton>

      {error !== null ? (
        <FramedText
          meta={{
            id: 'presets.editor.error',
            page: 'presets',
            role: 'text',
            label: '编辑校验错误提示',
            layout: { ...EDITOR_ERROR_BOX, height: errorHeight },
            zIndex: 30,
            notes: '保存失败提示；安卓用 TextInputLayout error',
          }}
          className="zs-error zs-editor-error"
          title={error}
        >
          <span ref={errorContent}>{error}</span>
        </FramedText>
      ) : null}

      {avatarError !== null && error === null ? (
        <FramedText
          meta={{ id: 'presets.editor.avatarError', page: 'presets', role: 'text', label: '头像上传错误提示', layout: { ...EDITOR_ERROR_BOX }, zIndex: 30 }}
          className="zs-error"
        >
          {avatarError}
        </FramedText>
      ) : null}

      {notice !== null && error === null ? (
        <FramedText meta={{ id: 'presets.editor.notice', page: 'presets', role: 'text', label: '世界书导入提示', layout: { ...EDITOR_NOTICE_BOX }, zIndex: 30 }} className="zs-import-notice" title={notice}>
          {notice.split('\n')[0]}
        </FramedText>
      ) : null}

      <FramedBox
        meta={{
          id: 'presets.editor.form',
          page: 'presets',
          role: 'container',
          label: '角色卡表单滚动区',
          layout: { ...FORM_SCROLL_BOX },
          zIndex: 4,
          notes: '可滚动表单容器；安卓用 NestedScrollView',
        }}
        className="zs-scroll"
        style={{ transform: `translateY(${errorOffset}px)`, height: FORM_SCROLL_BOX.height - errorOffset }}
      >
        <div className="zs-canvas" style={{ height: canvasHeight }}>
          <FramedText
            meta={{
              id: 'presets.editor.nameLabel',
              page: 'presets',
              role: 'text',
              label: '名称标签',
              layout: { ...FIELD_NAME_LABEL_BOX },
              zIndex: 5,
              notes: '字段标签；安卓用 TextInputLayout hint',
            }}
            className="zs-field-label"
          >
            名称
          </FramedText>
          <FramedInput
            meta={{
              id: 'presets.editor.nameInput',
              page: 'presets',
              role: 'input',
              label: '名称',
              layout: { ...FIELD_NAME_BOX },
              zIndex: 5,
              notes: '联系人名称；安卓用 TextInputEditText',
            }}
            className="zs-input"
            placeholder="例如：猫娘"
            value={draft.name}
            onValueChange={(value) => {
              patch({ name: value });
            }}
          />

          <FramedText
            meta={{
              id: 'presets.editor.avatarLabel',
              page: 'presets',
              role: 'text',
              label: '头像标签',
              layout: { ...FIELD_AVATAR_LABEL_BOX },
              zIndex: 5,
              notes: '字段标签；安卓用 TextInputLayout hint',
            }}
            className="zs-field-label"
          >
            头像（上传图片、emoji 或图片地址）
          </FramedText>
          <FramedBox
            meta={{ id: 'presets.editor.avatarPreview', page: 'presets', role: 'icon', label: '头像预览', layout: { x: 27, y: 196, width: 40, height: 40 }, zIndex: 6 }}
            className="zs-avatar-preview"
          >
            {draft.avatar && /^(data:image\/|https?:\/\/|blob:)/i.test(draft.avatar)
              ? <img src={draft.avatar} alt="" />
              : (draft.avatar !== undefined && draft.avatar.trim() !== ''
                  ? draft.avatar.trim()
                  : (draft.name.trim().slice(0, 1) === '' ? '联' : draft.name.trim().slice(0, 1)))}
          </FramedBox>
          <FramedInput
            meta={{
              id: 'presets.editor.avatarInput',
              page: 'presets',
              role: 'input',
              label: '头像',
              layout: { x: 86, y: 196, width: 198, height: 40 },
              zIndex: 5,
              notes: '头像 emoji / URL；安卓用 TextInputEditText',
            }}
            className="zs-input"
            placeholder="🐱 或 https://…"
            value={draft.avatar?.startsWith('data:image/') ? '' : (draft.avatar ?? '')}
            onValueChange={(value) => {
              setAvatarError(null);
              patch({ avatar: value.trim() === '' ? undefined : value });
            }}
          />
          <FramedButton
            meta={{ id: 'presets.editor.avatarUpload', page: 'presets', role: 'button', label: '上传头像', layout: { x: 292, y: 196, width: 82, height: 40 }, zIndex: 6 }}
            className="zs-button zs-button-ghost"
            onClick={() => avatarInput.current?.click()}
          >上传</FramedButton>
          {draft.avatar ? (
            <FramedButton
              meta={{ id: 'presets.editor.avatarRemove', page: 'presets', role: 'button', label: '移除头像', layout: { x: 292, y: 242, width: 82, height: 28 }, zIndex: 6 }}
              className="zs-button zs-button-danger"
              onClick={() => patch({ avatar: undefined })}
            >移除头像</FramedButton>
          ) : null}
          <input
            ref={avatarInput}
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            aria-label="选择头像图片"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = '';
              if (!file) return;
              setAvatarError(null);
              setAvatarFile(file);
            }}
          />
          {avatarFile ? <ImageCropDialog file={avatarFile} kind="avatar" onCancel={() => setAvatarFile(null)} onConfirm={(avatar) => {
            patch({ avatar });
            setAvatarFile(null);
          }} /> : null}

          <FramedButton meta={{ id: 'presets.editor.modelConfig', page: 'presets', role: 'button', label: '模型配置', layout: { ...MODEL_CONFIG_BUTTON_BOX }, zIndex: 6 }}
            className="zs-button zs-button-ghost zs-model-config-button" disabled={saving} onClick={() => setModelOpen(true)}>模型配置</FramedButton>

          <FramedText
            meta={{
              id: 'presets.editor.tagsLabel',
              page: 'presets',
              role: 'text',
              label: '标签标签',
              layout: { ...FIELD_TAGS_LABEL_BOX },
              zIndex: 5,
              notes: '字段标签；安卓用 TextInputLayout hint',
            }}
            className="zs-field-label"
          >
            标签（逗号分隔）
          </FramedText>
          <FramedInput
            meta={{
              id: 'presets.editor.tagsInput',
              page: 'presets',
              role: 'input',
              label: '标签',
              layout: { ...FIELD_TAGS_BOX },
              zIndex: 5,
              notes: '标签，逗号分隔；安卓用 TextInputEditText + ChipGroup',
            }}
            className="zs-input"
            placeholder="日常，中文"
            value={(draft.tags ?? []).join('，')}
            onValueChange={(value) => {
              patch({ tags: splitKeywords(value) });
            }}
          />

          <FramedText
            meta={{
              id: 'presets.editor.descriptionLabel',
              page: 'presets',
              role: 'text',
              label: '简介标签',
              layout: { ...FIELD_DESC_LABEL_BOX },
              zIndex: 5,
              notes: '字段标签；安卓用 TextInputLayout hint',
            }}
            className="zs-field-label"
          >
            简介
          </FramedText>
          <FramedTextarea
            meta={{
              id: 'presets.editor.descriptionInput',
              page: 'presets',
              role: 'input',
              label: '简介',
              layout: { ...FIELD_DESC_BOX },
              zIndex: 5,
              notes: '角色简介；安卓用 TextInputEditText + maxLines',
            }}
            className="zs-input zs-textarea"
            placeholder="一句话介绍这位角色"
            value={draft.description ?? ''}
            onValueChange={(value) => {
              patch({ description: value });
            }}
          />

          <FramedText
            meta={{
              id: 'presets.editor.systemPromptLabel',
              page: 'presets',
              role: 'text',
              label: '系统提示词标签',
              layout: { ...FIELD_PROMPT_LABEL_BOX },
              zIndex: 5,
              notes: '字段标签；安卓用 TextInputLayout hint',
            }}
            className="zs-field-label"
          >
            系统提示词
          </FramedText>
          <FramedTextarea
            meta={{
              id: 'presets.editor.systemPromptInput',
              page: 'presets',
              role: 'input',
              label: '系统提示词',
              layout: { ...FIELD_PROMPT_BOX },
              zIndex: 5,
              notes: '角色设定正文，对话时作为 system 提示；安卓用 TextInputEditText',
            }}
            className="zs-input zs-textarea"
            placeholder="你是一只…"
            value={draft.systemPrompt}
            onValueChange={(value) => {
              patch({ systemPrompt: value });
            }}
          />

          <IntimacyControl key={draft.id} presetId={draft.id} top={INTIMACY_TOP} />
          <PresetAutomationEditor draft={draft} top={AUTOMATION_TOP} onChange={onChange} />

          <FramedHeading
            level={2}
            meta={{
              id: 'presets.editor.worldBookTitle',
              page: 'presets',
              role: 'text',
              label: '世界书区块标题',
              layout: { ...WORLD_BOOK_TITLE_BOX, y: WORLD_BOOK_TITLE_BOX.y + worldBookOffset },
              zIndex: 5,
              notes: '区块标题；安卓用 TextView',
            }}
            className="zs-section-title"
          >
            世界书
          </FramedHeading>

          <FramedButton
            meta={{
              id: 'presets.editor.importWorldBookButton',
              page: 'presets',
              role: 'button',
              label: '导入世界书到角色卡',
              layout: { ...IMPORT_WORLD_BOOK_EDITOR_BOX, y: IMPORT_WORLD_BOOK_EDITOR_BOX.y + worldBookOffset },
              zIndex: 6,
              notes: '把主流世界书 JSON 合并到当前角色卡；安卓用 MaterialButton + ACTION_OPEN_DOCUMENT',
            }}
            className="zs-button zs-button-ghost"
            onClick={onImportWorldBook}
            disabled={importing}
          >
            导入世界书
          </FramedButton>

          <FramedButton
            meta={{
              id: 'presets.editor.addEntryButton',
              page: 'presets',
              role: 'button',
              label: '添加世界书条目',
              layout: { ...ADD_ENTRY_BUTTON_BOX, y: ADD_ENTRY_BUTTON_BOX.y + worldBookOffset },
              zIndex: 6,
              notes: '新增世界书条目；安卓用 MaterialButton',
            }}
            className="zs-button zs-button-ghost"
            onClick={addEntry}
          >
            添加条目
          </FramedButton>

          {entries.map((entry, index) => {
            const box = worldBookEntryBoxes(index, worldBookOffset);
            const keywordsText = rawKeywords[entry.id] ?? entry.keywords.join('，');
            return (
              <div key={entry.id}>
                <FramedBox
                  meta={{
                    id: `presets.editor.entry.${index}.card`,
                    page: 'presets',
                    role: 'container',
                    label: `第 ${index + 1} 条世界书`,
                    layout: { ...box.card },
                    zIndex: 4,
                    notes: '世界书条目卡片；安卓用 MaterialCardView',
                  }}
                  className="zs-card zs-entry-card"
                />

                <FramedText
                  meta={{
                    id: `presets.editor.entry.${index}.keywordsLabel`,
                    page: 'presets',
                    role: 'text',
                    label: `关键词标签（第 ${index + 1} 条）`,
                    layout: { ...box.keywordsLabel },
                    zIndex: 5,
                    notes: '字段标签；安卓用 TextInputLayout hint',
                  }}
                  className="zs-field-label"
                >
                  {`关键词（第 ${index + 1} 条）`}
                </FramedText>
                <FramedInput
                  meta={{
                    id: `presets.editor.entry.${index}.keywordsInput`,
                    page: 'presets',
                    role: 'input',
                    label: `关键词（第 ${index + 1} 条）`,
                    layout: { ...box.keywordsInput },
                    zIndex: 5,
                    notes: '关键词，逗号分隔；安卓用 TextInputEditText',
                  }}
                  className="zs-input"
                  placeholder="喵，猫"
                  value={keywordsText}
                  onValueChange={(value) => {
                    setRawKeywords((prev) => ({ ...prev, [entry.id]: value }));
                    patchEntry(index, { keywords: splitKeywords(value) });
                  }}
                />

                <FramedSwitch
                  meta={{ id: `presets.editor.entry.${index}.constantSwitch`, page: 'presets', role: 'button', label: `第 ${index + 1} 条常驻开关`, layout: box.constant, zIndex: 5, props: ['checked'] }}
                  className={entry.constant ? 'zs-switch zs-switch-on' : 'zs-switch'}
                  checked={entry.constant === true}
                  onToggle={next => patchEntry(index, { constant: next })}
                >常驻</FramedSwitch>

                <FramedText meta={{ id: `presets.editor.entry.${index}.rules`, page: 'presets', role: 'text', label: `第 ${index + 1} 条附加规则`, layout: box.rules, zIndex: 5 }} className="zs-entry-rules">
                  {[entry.name, entry.secondaryKeywords?.length ? `次级关键词（${entry.selectiveLogic ?? 'andAny'}）：${entry.secondaryKeywords.join('，')}` : '', entry.caseSensitive ? '区分大小写' : '', entry.matchWholeWords ? '全词匹配' : ''].filter(Boolean).join(' · ')}
                </FramedText>

                <FramedText
                  meta={{
                    id: `presets.editor.entry.${index}.contentLabel`,
                    page: 'presets',
                    role: 'text',
                    label: `内容标签（第 ${index + 1} 条）`,
                    layout: { ...box.contentLabel },
                    zIndex: 5,
                    notes: '字段标签；安卓用 TextInputLayout hint',
                  }}
                  className="zs-field-label"
                >
                  {`内容（第 ${index + 1} 条）`}
                </FramedText>
                <FramedTextarea
                  meta={{
                    id: `presets.editor.entry.${index}.contentInput`,
                    page: 'presets',
                    role: 'input',
                    label: `内容（第 ${index + 1} 条）`,
                    layout: { ...box.contentInput },
                    zIndex: 5,
                    notes: '命中关键词时注入的内容；安卓用 TextInputEditText',
                  }}
                  className="zs-input zs-textarea"
                  placeholder="命中关键词后注入的世界书内容"
                  value={entry.content}
                  onValueChange={(value) => {
                    patchEntry(index, { content: value });
                  }}
                />

                <FramedSwitch
                  meta={{
                    id: `presets.editor.entry.${index}.enabledSwitch`,
                    page: 'presets',
                    role: 'button',
                    label: `第 ${index + 1} 条启用开关`,
                    layout: { ...box.enabled },
                    zIndex: 5,
                    props: ['checked'],
                    notes: '启用 / 停用该条；安卓用 SwitchMaterial',
                  }}
                  className={entry.enabled ? 'zs-switch zs-switch-on' : 'zs-switch'}
                  checked={entry.enabled}
                  onToggle={(next) => {
                    patchEntry(index, { enabled: next });
                  }}
                >
                  {entry.enabled ? '已启用' : '未启用'}
                </FramedSwitch>

                <FramedText
                  meta={{
                    id: `presets.editor.entry.${index}.priorityLabel`,
                    page: 'presets',
                    role: 'text',
                    label: `优先级标签（第 ${index + 1} 条）`,
                    layout: { ...box.priorityLabel },
                    zIndex: 5,
                    notes: '字段标签；安卓用 TextInputLayout hint',
                  }}
                  className="zs-field-label"
                >
                  优先级
                </FramedText>
                <FramedInput
                  meta={{
                    id: `presets.editor.entry.${index}.priorityInput`,
                    page: 'presets',
                    role: 'input',
                    label: `优先级（第 ${index + 1} 条）`,
                    layout: { ...box.priorityInput },
                    zIndex: 5,
                    notes: '数值越大越先注入；安卓用 TextInputEditText number',
                  }}
                  className="zs-input"
                  type="number"
                  value={String(entry.priority)}
                  onValueChange={(value) => {
                    const parsed = Number.parseInt(value, 10);
                    patchEntry(index, { priority: Number.isFinite(parsed) ? parsed : 0 });
                  }}
                />

                <FramedButton
                  meta={{
                    id: `presets.editor.entry.${index}.removeButton`,
                    page: 'presets',
                    role: 'button',
                    label: `删除第 ${index + 1} 条世界书`,
                    layout: { ...box.remove },
                    zIndex: 5,
                    notes: '删除该条；安卓用 MaterialButton',
                  }}
                  className="zs-button zs-button-danger"
                  onClick={() => {
                    removeEntry(index);
                  }}
                >
                  删除条目
                </FramedButton>
              </div>
            );
          })}

        </div>
      </FramedBox>
    </FramedBox>
  );
}
